import React, { useRef, useEffect, useState, useCallback, useMemo, memo } from 'react';
import { pick, prop, equals } from 'ramda';
import { useDeviceSelector } from 'js/store/hooks';
import { usePrevious } from 'js/hooks/usePrevious';
import { clsb, clsx } from 'js/utils/classNames.util';
import { withSwipe } from 'js/components/hocs/withSwipe';

import * as hooks from './hooks';
import * as Types from './types';
import * as helpers from './helpers';
import * as actions from './store/actions';
import { defaultOptions } from './constants';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { CarouselItem } from './components/CarouselItem';
import { CarouselControls } from './components/CarouselControls';
import { ISwiperProps } from 'js/components/hocs/withSwipe/types';
import { CarouselNumeration } from './components/CarouselNumeration';
import { withDynamicReducer, useDynamicReducerDispatch, useDynamicReducerSelector } from './store';

import style from './Carousel.scss';

/**
 * @typedef CarouselProps
 * @type {Object}
 * @property {React.ReactNode} [children] - carousel items.
 * @property {string} [className] - carousel wrapper className.
 * @property {Object} [classNames] - custom classNames for carousel elements.
 * @property {number} [gap=5] - gap between carousel items, in px.
 * @property {number} itemsPerView - number of items per view. User see items plus tail.
 * @property {boolean} [loop=false]  - whether the carousel should loop.
 * @property {boolean} [navigation=true] - whether the carousel should have navigation controls.
 * @property {boolean} [numeration=false] - whether the carousel should have numeration for items. If true, totalItems are limited to 99.
 * @property {number} [ratio] - aspect ratio of the carousel item.
 * @property {boolean} [reserveTailSpace=false] - if true, show empty space as tail is there is only one view.
 * @property {boolean} [tailWidth=5] - width of the carousel tail, in %.
 * @property {number} [totalItems] - total number of items in the carousel.
 *
 * @param props {CarouselProps & {swipeStart: Function, swipeDeltaX: number}}
 */
export const CarouselBare: React.FC<Types.ICarouselProps & ISwiperProps> = (props) => {
    const {
        ratio,
        children,
        className,
        totalItems,
        itemsPerView,
        gap = defaultOptions.gap,
        loop = defaultOptions.loop,
        tailWidth = defaultOptions.tailWidth,
        navigation = defaultOptions.navigation,
        numeration = defaultOptions.numeration,
        reserveTailSpace = defaultOptions.reserveTailSpace,
        swipeStart, swipeDeltaX = 0 // ISwiperProps
    } = props;

    const isMounted = useIsMounted();
    const dispatch = useDynamicReducerDispatch();
    const classNames = useMemo(() => props.classNames || {}, [props.classNames]);
    const isTouchDevice = useDeviceSelector(prop('isTouchDevice'));
    const { prevSlide, activeSlide, containerShift, itemWidth, direction, restart } = useDynamicReducerSelector(pick([
        'prevSlide', 'activeSlide', 'containerShift', 'itemWidth', 'direction', 'restart'
    ]));

    const wrapperWidthRef = useRef<number>(0);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const hiddenRef = useRef<HTMLSpanElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [focusedItem, setFocusedItem] = useState<number | null>(null);
    const prevActiveSlide = usePrevious(activeSlide, 0);
    const prevSwipeDeltaX = usePrevious(swipeDeltaX, 0);
    const prevFocusedItem = usePrevious(focusedItem, null);

    const items = useMemo((): Exclude<React.ReactNode, boolean | null | undefined>[] => {
        const sliceEnd = totalItems && numeration && totalItems > 99 ? 100 : totalItems;

        return React.Children.toArray(children).slice(0, sliceEnd);
    }, [children, totalItems, numeration]);

    const slidesCount = items.length;
    const previousItems = usePrevious(items);
    const isOnlyOneView = slidesCount <= itemsPerView;

    const getVisualItems = useCallback((
        prevItems?: Types.IVisualItem[], prevItem?: number
    ): Types.IVisualItem[] => {
        if (isOnlyOneView) {
            return items.map((content, i) => ({ content, realIndex: i, virtualKey: i }));
        }

        const loopFrom = loop ? -slidesCount : 0;
        const loopTo = loop ? 2 * slidesCount : slidesCount;
        const { from, to } = helpers.getVirtualIndexes({ loop, activeSlide, itemsPerView, slidesCount });

        let slidesToRender = [];

        for (let i = loopFrom; i < loopTo; i++) {
            if (i >= from && i <= to) {
                const realIndex = helpers.getSlideIndex(i, slidesCount);
                const slide = items[realIndex];
                const content = React.isValidElement(slide) ? React.cloneElement(slide) : slide;

                slidesToRender.push({
                    content,
                    realIndex,
                    virtualKey: i
                });
            }
        }

        if (loop && prevItems && prevItem !== undefined) {
            slidesToRender = helpers.getItemsWithModifiedKeys({
                activeSlide, direction, prevItems, slidesCount,
                nextItems: slidesToRender, prevSlide: prevItem
            });
        }

        return slidesToRender;
    }, [activeSlide, items, loop, itemsPerView, slidesCount, isOnlyOneView, direction]);

    const [visualItems, setVisualItems] = useState(getVisualItems());
    const handleSwipe = hooks.useHandleSwipe(containerRef.current);
    const updateDimensions = hooks.useUpdateDimensions(containerRef.current);
    const shiftCarouselPosition = hooks.useShiftCarouselPosition(containerRef.current);

    const onKeyUp = useCallback((event: React.KeyboardEvent<HTMLDivElement>): void => {
        const { key } = event;

        if (key === 'Escape') {
            setFocusedItem(null);
            hiddenRef.current?.focus();
        }
    }, []);

    const onSwipeStart = useCallback((event: React.SyntheticEvent): void => {
        if (isOnlyOneView) {
            return;
        }

        swipeStart?.(event);
    }, [isOnlyOneView, swipeStart]);

    const onTransitionEnd = useCallback((): void => {
        containerRef.current?.classList.remove('animating');
    }, []);

    useEffect(() => {
        dispatch(actions.updateSlidesCount(slidesCount));
    }, [dispatch, slidesCount]);

    useEffect(() => {
        dispatch(actions.updateCarouselConfig({
            gap, itemsPerView, tailWidth, numeration,
            loop, ratio, navigation, totalItems, reserveTailSpace
        }));
    }, [
        dispatch, navigation, totalItems, gap, reserveTailSpace,
        itemsPerView, tailWidth, numeration, loop, ratio
    ]);

    useEffect(() => {
        isMounted() && updateDimensions({ isOnlyOneView, reserveTailSpace, isSilent: false });
    }, [isMounted, updateDimensions, isOnlyOneView, reserveTailSpace]);

    // set initial item shift
    useEffect(() => {
        const container = containerRef.current;

        if (!container || !itemWidth || !loop || !(activeSlide === 0 && prevSlide === 0)) {
            return;
        }

        const initialItemShift = helpers.getItemShift({
            container, loop, itemWidth, itemsPerView, gap,
            activeSlide: 0, containerShift: 0, slidesCount
        });

        dispatch(actions.updateItemShift(initialItemShift));
    }, [
        dispatch, activeSlide, prevSlide, slidesCount,
        itemWidth, loop, itemsPerView, gap
    ]);

    // restart while carousel children are changed
    useEffect(() => {
        if (previousItems) {
            const keys = items.map(item => React.isValidElement(item) ? item.key : item);
            const prevKeys = previousItems.map(item => React.isValidElement(item) ? item.key : item);

            if (!equals(prevKeys, keys)) {
                dispatch(actions.toggleRestart(true));
            }
        }
    }, [items, previousItems, dispatch]);

    // handle slide change or carousel restart
    useEffect(() => {
        const container = containerRef.current;

        if (!container) {
            return;
        }

        if (prevActiveSlide !== activeSlide || restart) {
            const nextItems = getVisualItems(visualItems, prevActiveSlide || 0);

            shiftCarouselPosition();
            setVisualItems(nextItems);
            restart && dispatch(actions.toggleRestart(false));
        }
    }, [
        shiftCarouselPosition, prevActiveSlide, activeSlide,
        getVisualItems, visualItems, dispatch, restart
    ]);

    // handle focused item change
    useEffect(() => {
        if (typeof prevFocusedItem !== 'number' || typeof focusedItem !== 'number') {
            return;
        }

        if (focusedItem !== prevFocusedItem) {
            dispatch(actions.moveRight(1));
        }
    }, [focusedItem, prevFocusedItem, dispatch]);

    // handle swipe
    useEffect(() => {
        if (isOnlyOneView || !prevSwipeDeltaX) {
            return;
        }

        handleSwipe(swipeDeltaX, prevSwipeDeltaX);
    }, [isOnlyOneView, swipeDeltaX, prevSwipeDeltaX, handleSwipe]);

    // handle resizing
    useEffect(() => {
        const container = containerRef.current;
        const resizeObserver = new ResizeObserver(([entry]) => {
            if (wrapperWidthRef.current !== entry.contentRect.width) {
                updateDimensions({ isOnlyOneView, reserveTailSpace, isSilent: true });
            }

            wrapperWidthRef.current = entry.contentRect.width;
        });

        container && resizeObserver.observe(container);

        return () => {
            container && resizeObserver.unobserve(container);
        };
    }, [updateDimensions, isOnlyOneView, reserveTailSpace]);

    if (!slidesCount) {
        return null;
    }

    return (
        <>
            <div
                ref={wrapperRef}
                onKeyUp={onKeyUp}
                {...isTouchDevice ? { onTouchStart: onSwipeStart } : { onMouseDown: onSwipeStart }}
                className={clsx([clsb([style, classNames], ['carouselWrapper']), className, 'cms-carousel-wrapper'])}
            >
                {navigation && !isOnlyOneView && (
                    <CarouselControls
                        classNames={classNames}
                        setFocusedItem={setFocusedItem}
                    />
                )}
                <div
                    ref={containerRef}
                    onTransitionEnd={onTransitionEnd}
                    style={{
                        gap: `${gap}px`,
                        ...!isOnlyOneView && { transform: `translateX(${containerShift}%)` }
                    }}
                    className={clsx([
                        clsb([style, classNames], ['carouselContainer']),
                        'cms-carousel-container'
                    ])}
                >
                    {visualItems.map(({ content, realIndex, virtualKey }) => (
                        <CarouselItem
                            key={virtualKey}
                            realIndex={realIndex}
                            classNames={classNames}
                            focusedItem={focusedItem}
                            setFocusedItem={setFocusedItem}
                            {...numeration && { className: clsx([
                                clsb([style, classNames], ['itemWithNumeration']), 'cms-carousel-item-with-numeration'])
                            }}
                        >
                            {numeration && (
                                <CarouselNumeration
                                    classNames={classNames}
                                    numerationIndex={realIndex + 1}
                                />
                            )}
                            {content}
                        </CarouselItem>
                    ))}
                </div>
            </div>
            <span ref={hiddenRef} tabIndex={-1} aria-hidden={true} />
        </>
    );
};

export const Carousel = withDynamicReducer()(withSwipe()(memo(CarouselBare)));
